import BaseController from "confinus_ui/base_controller";
import NotificationsSubscription from "channels/notifications_subscription";

export default class extends BaseController {
  static targets = ["form", "textarea", "submitButton"];
  static values = {
    threadId: String,
    currentUserId: Number,
  };

  declare readonly formTarget: HTMLFormElement;
  declare readonly textareaTarget: HTMLTextAreaElement;
  declare readonly submitButtonTarget: HTMLButtonElement;
  declare readonly threadIdValue: string;
  declare readonly currentUserIdValue: number;

  private typingTimeout: number | null = null;
  private isTyping: boolean = false;
  private notificationsSubscription!: NotificationsSubscription;

  connect() {
    console.log("Message form controller connected");
    this.notificationsSubscription = NotificationsSubscription.getInstance();
  }

  // Track visit when user focuses the textarea
  onFocus() {
    this.markThreadAsVisited();
  }

  private markThreadAsVisited() {
    const url = `/account/messages/threads/${this.threadIdValue}/mark_visited`;

    fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.getCsrfToken(),
        "Content-Type": "application/json",
      },
    }).catch((error) => {
      console.error("Error marking thread as visited:", error);
    });
  }

  private getCsrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") || "" : "";
  }

  disconnect() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
  }

  onInput() {
    // Broadcast typing indicator
    this.broadcastTyping(true);

    // Clear existing timeout
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    // Set timeout to stop typing after 3 seconds of inactivity
    this.typingTimeout = window.setTimeout(() => {
      this.broadcastTyping(false);
    }, 3000);
  }

  broadcastTyping(isTyping: boolean) {
    if (this.isTyping === isTyping) return; // Don't broadcast if state hasn't changed

    this.isTyping = isTyping;

    console.log("Broadcasting typing indicator:", {
      thread_id: this.threadIdValue,
      user_id: this.currentUserIdValue,
      is_typing: isTyping,
    });

    this.notificationsSubscription.perform("typing", {
      thread_id: this.threadIdValue,
      user_id: this.currentUserIdValue,
      is_typing: isTyping,
    });
  }

  async submit(event: Event) {
    event.preventDefault();

    // Stop typing indicator
    this.broadcastTyping(false);
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }

    const message = this.textareaTarget.value.trim();
    if (!message) return;

    // Get form data BEFORE disabling fields
    const formData = new FormData(this.formTarget);

    // Debug: Log what's being sent
    console.log("Form action:", this.formTarget.action);
    console.log("Form data entries:");
    for (let [key, value] of formData.entries()) {
      console.log(`  ${key}: ${value}`);
    }

    // Now disable form while sending
    this.textareaTarget.disabled = true;
    this.submitButtonTarget.disabled = true;
    this.submitButtonTarget.textContent = "Sending...";

    try {
      const response = await fetch(this.formTarget.action, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "text/javascript, application/javascript",
        },
      });

      if (response.ok) {
        // Clear the form
        this.textareaTarget.value = "";

        // Don't mark thread as visited here - sending a message doesn't mean you've read messages
        // The thread is already marked as visited when the user focuses on the textarea (onFocus)

        // The server will broadcast the message via ActionCable
        // and it will be inserted into the DOM by the notifications controller
      } else {
        console.error("Failed to send message:", response.statusText);
        alert("Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("An error occurred. Please try again.");
    } finally {
      // Re-enable form
      this.textareaTarget.disabled = false;
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.textContent = "Send";
      this.textareaTarget.focus();
    }
  }

  onKeyPress(event: KeyboardEvent) {
    // Submit on Enter (but not Shift+Enter)
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.submit(event);
    }
  }
}
